import React, { useEffect, useState } from 'react';
import { useMutation, useQuery } from 'urql';
import { useKeycloak } from '../../shared/auth/KeycloakProvider.tsx';
import { AdminSeminar } from '../../gql/graphql.ts';
import { graphql } from '../../gql';
import {
  InputStateWrapper,
  InputStateWrapper as HookedInput,
} from '../form-components/InputStateWrapper.tsx';
import { useFieldArray, useForm } from 'react-hook-form';
import { useLocation, useNavigate } from '@tanstack/react-router';
import { Button } from '@progress/kendo-react-buttons';
import {
  Upload,
  UploadFileInfo,
  UploadFileStatus,
  UploadOnAddEvent,
  UploadOnRemoveEvent,
} from '@progress/kendo-react-upload';
import { PageTitle } from '../PageTitle.tsx';
import { Fieldset } from '../form-components/Fieldset.tsx';
import { CheckboxStateWrapper } from '../form-components/CheckboxStateWrapper.tsx';
import { DropdownStateWrapper } from '../form-components/DropdownStateWrapper.tsx';
import { DateTimePickerStateWrapper } from '../form-components/DateTimePickerStateWrapper.tsx';
import { HeroIcon } from '../../shared/HeroIcon.tsx';
import { NumericInputStateWrapper } from '../form-components/NumericInputStateWrapper.tsx';
import { EditorStateWrapper } from '../form-components/EditorStateWrapper.tsx';
import { cloneAndRemoveTypename } from '../../public/util.ts';
import { Input } from '../form-components/Input.tsx';
import { BackButton } from '../../shared/BackButton.tsx';
import {
  TabStrip,
  TabStripSelectEventArguments,
  TabStripTab,
} from '@progress/kendo-react-layout';
import { formFieldLabels } from '../fieldNameLabels.ts';
import { restApiUrl } from '../../shared/helpers/backendUrl';

type VisibilityState = 'visible' | 'unlisted' | 'hidden';

const visibilityOptions: Array<{ text: string; value: VisibilityState }> = [
  { text: 'Sichtbar', value: 'visible' },
  { text: 'Nicht gelistet', value: 'unlisted' },
  { text: 'Nicht sichtbar', value: 'hidden' },
];

function toVisibilityState(
  visible?: boolean,
  unlisted?: boolean | null,
): VisibilityState {
  if (!visible) return 'hidden';
  if (unlisted) return 'unlisted';
  return 'visible';
}

function fromVisibilityState(state: VisibilityState): {
  visible: boolean;
  unlisted: boolean;
} {
  switch (state) {
    case 'visible':
      return { visible: true, unlisted: false };
    case 'unlisted':
      return { visible: true, unlisted: true };
    case 'hidden':
    default:
      return { visible: false, unlisted: false };
  }
}

interface SeminarEditPageProps {
  companyId: string;
  seminarId?: string;
  btsSeminarId?: string;
}

const SeminarQuery = graphql(/* GraphQL */ `
  query seminar(
    $companyId: String!
    $seminarId: String
    $btsSeminarId: String
  ) {
    seminar(
      companyId: $companyId
      seminarId: $seminarId
      btsSeminarId: $btsSeminarId
    ) {
      id
      title
      subtitle
      startDate
      endDate
      registrationDate
      dateText
      canceled
      locationText
      price
      price2
      price3
      price4
      priceText
      priceText2
      priceText3
      priceText4
      minParticipants
      maxParticipants
      currentParticipants
      description
      description2
      description3
      description4
      description5
      shortDescription
      metaText1
      metaText2
      metaText3
      metaText4
      metaText5
      freetext1
      freetext2
      freetext3
      freetext4
      freetext5
      material
      prerequisites
      organizationalNotes
      infoNotice
      visible
      unlisted
      webinar
      webinarLink
      informationIntern
      informationExtern
      btsLocked
      passwords
      seminarNumber
      signUpFormId
      lecturers {
        name
      }
      images {
        blobId
        name
        size
      }
    }
  }
`);

const UpdateSeminarMutation = graphql(/* GraphQL */ `
  mutation updateSeminar(
    $companyId: String!
    $seminarId: String!
    $updatedValues: UpdateSeminarDto!
  ) {
    updateSeminar(
      companyId: $companyId
      seminarId: $seminarId
      updateValues: $updatedValues
    )
  }
`);

const SeminarSignUpFormsQuery = graphql(/* GraphQL */ `
  query seminarEditSignUpForms($companyId: String!) {
    companySignUpForms(companyId: $companyId) {
      id
      name
      isDefault
    }
  }
`);

const RemoveImageSeminarMutation = graphql(/* GraphQL */ `
  mutation removeImage(
    $companyId: String!
    $seminarId: String!
    $blobId: String!
  ) {
    removeImage(companyId: $companyId, seminarId: $seminarId, blobId: $blobId)
  }
`);

export const SeminarEditPage: React.FC<SeminarEditPageProps> = ({
  companyId,
  seminarId,
  btsSeminarId,
}) => {
  const [selected, setSelected] = React.useState<number>(0);
  // TODO: Error handling
  const [{ data, fetching }] = useQuery({
    query: SeminarQuery,
    variables: { companyId, seminarId, btsSeminarId },
    pause:
      companyId === undefined ||
      companyId === null ||
      (!seminarId && !btsSeminarId),
  });

  const [{ data: signUpFormsData }] = useQuery({
    query: SeminarSignUpFormsQuery,
    variables: { companyId },
    pause: companyId === undefined || companyId === null,
  });

  const signUpForms = signUpFormsData?.companySignUpForms ?? [];
  const defaultForm = signUpForms.find((f) => f.isDefault);
  const signUpFormOptions = signUpForms.map((f) => ({
    text: f.isDefault ? `${f.name} (Standard)` : f.name,
    value: f.id,
  }));

  const { control, handleSubmit, reset, formState } = useForm<
    AdminSeminar & { visibilityState: VisibilityState }
  >({
    mode: 'onChange',
    reValidateMode: 'onChange',
    defaultValues: {
      visibilityState: 'hidden',
    },
  });
  const { fields, append, remove } = useFieldArray({
    control,
    name: 'passwords',
  });

  const [files, setFiles] = useState<UploadFileInfo[]>([]);
  const [isFilesDirty, setIsFilesDirty] = useState<boolean>(false);

  useEffect(() => {
    if (!seminarId && btsSeminarId && data?.seminar.id) {
      window.history.replaceState(
        null,
        '',
        `/admin/seminars/edit/${data?.seminar.id}`,
      );
    }
  }, [seminarId, btsSeminarId, data?.seminar.id]);

  useEffect(() => {
    if (!fetching && data?.seminar && signUpFormsData) {
      const withoutTypename =
        data?.seminar && cloneAndRemoveTypename(data?.seminar);
      // Sync server seminar images and form fields into editable state on load.
      // eslint-disable-next-line react-hooks/set-state-in-effect
      setFiles(
        withoutTypename?.images.map((img) => ({
          uid: img.blobId,
          extension: '.jpg',
          name: img.name,
          size: img.size,
          status: UploadFileStatus.Selected,
          progress: 0,
        })),
      );

      //delete images and id
      const formData = {
        ...withoutTypename,
        images: undefined,
        id: undefined,
        lecturers: undefined,
      };
      reset({
        ...formData,
        // null means the default form (the API never references it by id),
        // so preselect the default option in the dropdown.
        signUpFormId: data.seminar.signUpFormId ?? defaultForm?.id,
        startDate: new Date(data.seminar.startDate),
        endDate: new Date(data.seminar.endDate),
        registrationDate:
          data.seminar.registrationDate &&
          new Date(data.seminar.registrationDate),
        visibilityState: toVisibilityState(
          data.seminar.visible,
          data.seminar.unlisted,
        ),
      });
    }
  }, [fetching, signUpFormsData]);

  const [, updateSeminar] = useMutation(UpdateSeminarMutation);
  const [, removeImage] = useMutation(RemoveImageSeminarMutation);
  const navigate = useNavigate();
  const cameFromPublic =
    useLocation({
      select: (l) => new URLSearchParams(l.searchStr ?? '').get('from'),
    }) === 'public';
  const backHref = cameFromPublic ? `/seminar/${seminarId}` : '/admin/seminars';
  const { token } = useKeycloak();

  const onFormSubmit = async (
    value: AdminSeminar & { visibilityState: VisibilityState },
  ) => {
    if (seminarId) {
      for (const file of files) {
        if (file.getRawFile && file.getRawFile()) {
          const formData = new FormData();
          formData.append('image', file.getRawFile());
          await fetch(restApiUrl(`/seminar/${seminarId}/images/`), {
            method: 'POST',
            body: formData,
            headers: token ? { Authorization: `Bearer ${token}` } : undefined,
          });
        }
      }
      if (data?.seminar.images) {
        for (const image of data.seminar.images) {
          if (!files.find((f) => f.uid === image.blobId)) {
            await removeImage({
              seminarId: seminarId,
              companyId: companyId,
              blobId: image.blobId,
            });
          }
        }
      }
      updateSeminar({
        companyId,
        seminarId,
        updatedValues: {
          btsLocked: value.btsLocked,
          canceled: value.canceled,
          currentParticipants: value.currentParticipants,
          description: value.description,
          description2: value.description2,
          description3: value.description3,
          description4: value.description4,
          description5: value.description5,
          metaText1: value.metaText1,
          metaText2: value.metaText2,
          metaText3: value.metaText3,
          metaText4: value.metaText4,
          metaText5: value.metaText5,
          material: value.material,
          prerequisites: value.prerequisites,
          organizationalNotes: value.organizationalNotes,
          shortDescription: value.shortDescription,
          infoNotice: value.infoNotice,
          freetext1: value.freetext1,
          freetext2: value.freetext2,
          freetext3: value.freetext3,
          freetext4: value.freetext4,
          freetext5: value.freetext5,
          endDate: value.endDate,
          locationText: value.locationText,
          maxParticipants: value.maxParticipants,
          minParticipants: value.minParticipants,
          passwords: value.passwords,
          price: value.price,
          price2: value.price2,
          price3: value.price3,
          price4: value.price4,
          priceText: value.priceText,
          priceText2: value.priceText2,
          priceText3: value.priceText3,
          priceText4: value.priceText4,
          registrationDate: value.registrationDate,
          dateText: value.dateText,
          startDate: value.startDate,
          title: value.title,
          subtitle: value.subtitle,
          visible: fromVisibilityState(value.visibilityState).visible,
          unlisted: fromVisibilityState(value.visibilityState).unlisted,
          webinar: value.webinar,
          webinarLink: value.webinarLink,
          informationIntern: value.informationIntern,
          informationExtern: value.informationExtern,
          // The default form is represented as null in the API: reads return
          // null for it (mapped to the default option on load), so choosing
          // the default option must send null, never the default form's id.
          signUpFormId:
            value.signUpFormId && value.signUpFormId !== defaultForm?.id
              ? value.signUpFormId
              : null,
        },
      }).then(() => {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        navigate({ to: backHref as any });
      });
    }
  };

  const { isValid, isDirty } = formState;

  const onAddFile = (event: UploadOnAddEvent) => {
    setFiles(
      event.newState.map((files) => ({
        ...files,
        status: UploadFileStatus.Selected,
      })),
    );
    setIsFilesDirty(true);
  };

  const onRemoveFile = (event: UploadOnRemoveEvent) => {
    setFiles(event.newState);
    setIsFilesDirty(true);
  };

  const onRemoveUploadedFile = async (files: UploadFileInfo[]) => {
    // To approve to kendo, that the file with the uid is removed
    return { uid: files[0].uid };
  };

  return (
    <>
      <PageTitle title="Seminar bearbeiten">
        <div className="flex w-full justify-between">
          <BackButton href={backHref} />
          <a
            href={`${window.__BTS_EMBED_BASE_PREFIX__ ?? ''}/seminar/${seminarId}`}
            target="_blank"
            className="flex items-center gap-1.5 border-b border-transparent text-base text-zinc-800 hover:border-black hover:text-black"
          >
            Zur öffentlichen Seite{' '}
            <HeroIcon
              name="ArrowTopRightOnSquare"
              className="inline-block h-5 w-5 shrink-0"
            />
          </a>
        </div>
      </PageTitle>
      <form
        onSubmit={handleSubmit(onFormSubmit)}
        className="k-form k-form-md relative flex w-full flex-col rounded-md bg-white shadow lg:w-full"
      >
        <Fieldset
          legend={`Allgemein`}
          description="Basisinformationen rund um das Seminar"
        >
          <div className="flex flex-col gap-4 md:col-span-9 [&_.k-form-field]:!mt-0">
            <DropdownStateWrapper
              name={'visibilityState'}
              label={'Sichtbarkeit'}
              control={control}
              options={visibilityOptions}
              rules={{ required: 'Sichtbarkeit muss ausgewählt sein!' }}
              required
            />
            <DropdownStateWrapper
              name={'signUpFormId'}
              label={'Anmeldeformular'}
              control={control}
              options={signUpFormOptions}
              hint={
                'Welches Anmeldeformular für dieses Seminar verwendet wird. "Standard" nutzt das Standard-Formular.'
              }
            />
            <CheckboxStateWrapper
              name={'canceled'}
              label={'Abgesagt'}
              control={control}
            />
            <CheckboxStateWrapper
              name={'btsLocked'}
              label={'BTS-Aktualisierungen sperren'}
              control={control}
            />
            <CheckboxStateWrapper
              name={'webinar'}
              label={'Online-Schulung (Webinar)'}
              control={control}
            />
            <HookedInput
              name={'webinarLink'}
              label={'Online-Link'}
              hint={
                'Wird nur für Teilnehmer, Dozenten und Mitarbeiter angezeigt.'
              }
              control={control}
              type="url"
            />
            <HookedInput
              name={'title'}
              label={'Seminartitel'}
              rules={{ required: 'Seminartitel muss ausgefüllt sein!' }}
              control={control}
            />
            <HookedInput
              name={'subtitle'}
              label={'Subtitel'}
              control={control}
            />
            <HookedInput
              name={'seminarNumber'}
              label={'Seminarnummer'}
              control={control}
              readOnly
            />
            <div className="grid grid-cols-3 gap-x-6">
              <DateTimePickerStateWrapper
                name={'startDate'}
                label={'Beginn'}
                control={control}
              />
              <DateTimePickerStateWrapper
                name={'endDate'}
                label={'Ende'}
                control={control}
              />
              <DateTimePickerStateWrapper
                name={'registrationDate'}
                label={'Anmeldeschluss'}
                control={control}
              />
            </div>
            <HookedInput
              name={'dateText'}
              label={'Datumstext'}
              hint={
                'Dieses wird als Datum angezeigt, sollte es leer sein, wird Start- und Enddatum angezeigt.'
              }
              control={control}
            />
            {data?.seminar?.lecturers.map(
              (lecturer, i) =>
                lecturer.name && (
                  <Input
                    key={'lecturer' + i}
                    value={lecturer.name}
                    id={'lecturers'}
                    type={'text'}
                    label={i === 0 ? 'Dozent(in)' : undefined}
                    readOnly
                  />
                ),
            )}

            <HookedInput
              name={'locationText'}
              label={'Ort'}
              control={control}
              readOnly
            />
            <div className="grid grid-cols-3 gap-x-6">
              <NumericInputStateWrapper
                name={'minParticipants'}
                label={'Minimale Teilnehmeranzahl'}
                control={control}
              />
              <NumericInputStateWrapper
                name={'maxParticipants'}
                label={'Maximale Teilnehmeranzahl'}
                control={control}
              />
              <NumericInputStateWrapper
                name={'currentParticipants'}
                label={'Aktuelle Teilnehmeranzahl'}
                control={control}
              />
            </div>
          </div>
        </Fieldset>
        <Fieldset
          legend={`Preise`}
          description="Modifizieren Sie die verschiedenen Preise und ihre Beschreibungen"
        >
          <div className="grid grid-cols-[30%_1fr] gap-4 md:col-span-9 [&_.k-form-field]:!mt-0">
            <NumericInputStateWrapper
              name={'price'}
              format={'c2'}
              label={'Preis'}
              control={control}
            />
            <HookedInput
              name={'priceText'}
              label={'Preis Beschreibung'}
              control={control}
            />
            <NumericInputStateWrapper
              name={'price2'}
              label={'Preis 2'}
              format={'c2'}
              control={control}
            />
            <HookedInput
              name={'priceText2'}
              label={'Preis Beschreibung 2'}
              control={control}
            />
            <NumericInputStateWrapper
              name={'price3'}
              label={'Preis 3'}
              format={'c2'}
              control={control}
            />
            <HookedInput
              name={'priceText3'}
              label={'Preis Beschreibung 3'}
              control={control}
            />
            <NumericInputStateWrapper
              name={'price4'}
              label={'Preis 4'}
              format={'c2'}
              control={control}
            />
            <HookedInput
              name={'priceText4'}
              label={'Preis Beschreibung 4'}
              control={control}
            />
          </div>
        </Fieldset>
        <Fieldset
          legend={`Beschreibungen und Webseiten-Inhalt`}
          description="Die Beschreibungen und Bilder tauchen auf der öffentlichen Webseite für Teilnehmer und Interessenten auf."
        >
          <div className={'md:col-span-9'}>
            <TabStrip
              selected={selected}
              onSelect={(e: TabStripSelectEventArguments) => {
                setSelected(e.selected);
              }}
              animation={false}
              className="[&_.k-active_.k-link]:!text-gray-500 [&_.k-link]:!text-bts-theme-primary focus:[&_.k-tabstrip-item]:!shadow-none"
            >
              <TabStripTab
                contentClassName={'[&_.k-form-field]:!mt-0'}
                title="Beschreibung"
              >
                <div className="flex flex-col gap-4">
                  <EditorStateWrapper
                    name={'description'}
                    label={formFieldLabels['description']}
                    control={control}
                  />
                  <EditorStateWrapper
                    name={'description2'}
                    label={formFieldLabels['description2']}
                    control={control}
                  />
                  <EditorStateWrapper
                    name={'description3'}
                    label={formFieldLabels['description3']}
                    control={control}
                  />
                  <EditorStateWrapper
                    name={'description4'}
                    label={formFieldLabels['description4']}
                    control={control}
                  />
                  <EditorStateWrapper
                    name={'description5'}
                    label={formFieldLabels['description5']}
                    control={control}
                  />
                  <EditorStateWrapper
                    name={'shortDescription'}
                    label={formFieldLabels['shortDescription']}
                    control={control}
                  />
                </div>
              </TabStripTab>
              <TabStripTab
                contentClassName={'[&_.k-form-field]:!mt-0'}
                title="Organisation"
              >
                <div className="flex flex-col gap-4">
                  <EditorStateWrapper
                    name={'material'}
                    label={formFieldLabels['material']}
                    control={control}
                  />
                  <EditorStateWrapper
                    name={'prerequisites'}
                    label={formFieldLabels['prerequisites']}
                    control={control}
                  />
                  <EditorStateWrapper
                    name={'organizationalNotes'}
                    label={formFieldLabels['organizationalNotes']}
                    control={control}
                  />
                  <EditorStateWrapper
                    name={'infoNotice'}
                    label={formFieldLabels['infoNotice']}
                    control={control}
                  />
                </div>
              </TabStripTab>
              <TabStripTab
                contentClassName={
                  '!grid grid-cols-[55%_1fr] [&_.k-form-field]:!mt-0'
                }
                title="Meta"
              >
                <div className="flex flex-col gap-4">
                  <InputStateWrapper
                    name={'metaText1'}
                    label={formFieldLabels['metaText1']}
                    control={control}
                  />
                  <InputStateWrapper
                    name={'metaText2'}
                    label={formFieldLabels['metaText2']}
                    control={control}
                  />
                  <InputStateWrapper
                    name={'metaText3'}
                    label={formFieldLabels['metaText3']}
                    control={control}
                  />
                  <InputStateWrapper
                    name={'metaText4'}
                    label={formFieldLabels['metaText4']}
                    control={control}
                  />
                  <InputStateWrapper
                    name={'metaText5'}
                    label={formFieldLabels['metaText5']}
                    control={control}
                  />
                </div>
              </TabStripTab>
              <TabStripTab
                contentClassName={'[&_.k-form-field]:!mt-0'}
                title="Freitext"
              >
                <div className="flex flex-col gap-4">
                  <EditorStateWrapper
                    name={'freetext1'}
                    label={formFieldLabels['freetext1']}
                    control={control}
                  />
                  <EditorStateWrapper
                    name={'freetext2'}
                    label={formFieldLabels['freetext2']}
                    control={control}
                  />
                  <EditorStateWrapper
                    name={'freetext3'}
                    label={formFieldLabels['freetext3']}
                    control={control}
                  />
                  <EditorStateWrapper
                    name={'freetext4'}
                    label={formFieldLabels['freetext4']}
                    control={control}
                  />
                  <EditorStateWrapper
                    name={'freetext5'}
                    label={formFieldLabels['freetext5']}
                    control={control}
                  />
                </div>
              </TabStripTab>
              <TabStripTab
                contentClassName={'[&_.k-form-field]:!mt-0'}
                title="Information"
              >
                <div className="flex flex-col gap-4">
                  <EditorStateWrapper
                    name={'informationExtern'}
                    label={formFieldLabels['informationExtern']}
                    hint={'Sichtbar für alle Nutzer.'}
                    control={control}
                  />
                  <EditorStateWrapper
                    name={'informationIntern'}
                    label={'Information intern'}
                    hint={
                      'Nur sichtbar für Teilnehmer, Dozenten und Mitarbeiter.'
                    }
                    control={control}
                  />
                </div>
              </TabStripTab>
              <TabStripTab
                contentClassName={
                  '!grid grid-cols-[55%_1fr] [&_.k-form-field]:!mt-0'
                }
                title="Bilder"
              >
                <div className="flex flex-col gap-4">
                  <Upload
                    accept="image/png, image/jpeg"
                    batch={false}
                    multiple={true}
                    withCredentials={false}
                    removeUrl={onRemoveUploadedFile}
                    files={files}
                    onAdd={onAddFile}
                    onRemove={onRemoveFile}
                  />
                  <p className="mt-1 text-sm leading-6 text-gray-600">
                    Für optimale Darstellung sollten die Bilder ein
                    Seitenverhältnis von 21:9 aufweisen und eine Breite von
                    mindestens 500 Pixel besitzen. Ein gutes Format wäre z.B.
                    1260 x 540 Pixel.
                  </p>
                </div>
              </TabStripTab>
            </TabStrip>
          </div>
        </Fieldset>

        <Fieldset
          legend={`Passwörter`}
          description="Hier können Sie ein oder mehrere Passwörter definieren, mit denen Teilnehmer sich für das Seminar anmelden können. Ohne eines der Passwörter ist dann keine Anmeldung mehr möglich."
        >
          <div className="flex flex-col gap-4 md:col-span-9 [&_.k-form-field]:!mt-0">
            {fields.map((field, i) => (
              <div key={i} className={'flex items-end justify-between gap-4'}>
                <HookedInput
                  wrapperClassName={'flex-1'}
                  key={field.id}
                  name={`passwords.${i}`}
                  control={control}
                  label={`Passwort ${i + 1}`}
                  rules={{ required: 'Passwort muss ausgefüllt sein!' }}
                />
                <Button
                  className="h-[38px] !shadow-sm [&>span.k-button-text]:flex [&>span.k-button-text]:items-center [&>span.k-button-text]:gap-1"
                  type={'button'}
                  fillMode={'outline'}
                  themeColor={'secondary'}
                  onClick={() => remove(i)}
                  title="Entfernen"
                >
                  <HeroIcon name="Trash" className="block h-5 w-5" />
                </Button>
              </div>
            ))}
            <Button
              type={'button'}
              themeColor={'secondary'}
              className="self-start !shadow-sm [&>span.k-button-text]:flex [&>span.k-button-text]:items-center [&>span.k-button-text]:gap-1"
              onClick={() => append('')}
              title="Passwort hinzufügen"
            >
              <HeroIcon name="PlusCircle" className="block h-5 w-5" />
              <div>Passwort hinzufügen</div>
            </Button>
          </div>
        </Fieldset>

        <div className="k-form-buttons !mt-0 justify-end rounded-md bg-gray-50 !px-8 !py-3 !text-right">
          <Button
            type={'submit'}
            themeColor={'primary'}
            className="!shadow-sm [&>span.k-button-text]:flex [&>span.k-button-text]:items-center [&>span.k-button-text]:gap-1"
            disabled={!isValid || (!isDirty && !isFilesDirty)}
            title="Speichern"
          >
            <HeroIcon name="CheckCircle" className="block h-5 w-5" />
            <div>Speichern</div>
          </Button>
        </div>
      </form>
    </>
  );
};
